import { apiGet, apiPatch, apiPost } from "./client";

export type PartyRoleFilter = "all" | "customer" | "supplier";

export type PartyRow = {
  id: string;
  code: string | null;
  name: string;
  isCustomer: boolean;
  isSupplier: boolean;
  priceListId: string | null;
  deactivatedAt: string | null;
  updatedAt: string;
  /** Open SO AR when customer; null when supplier-only */
  outstandingAr: string | null;
  /** Open PO AP when supplier; null when customer-only */
  outstandingAp: string | null;
  /** Back-compat: AR, else AP, else "0.00" */
  outstanding: string;
};

export type PartyListPage = {
  parties: PartyRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type PartyListSort = "name" | "code";

export function listParties(
  companyRef: string,
  opts?: {
    role?: PartyRoleFilter;
    includeDeactivated?: boolean;
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: PartyListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.role && opts.role !== "all") params.set("role", opts.role);
  if (opts?.includeDeactivated) params.set("all", "1");
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "name") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<PartyListPage>(
    `/c/${companyRef}/parties${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function getParty(companyRef: string, partyId: string) {
  return apiGet<{ party: PartyRow }>(
    `/c/${companyRef}/parties/${partyId}`,
  );
}

export function createParty(
  companyRef: string,
  body: {
    name: string;
    code?: string;
    isCustomer?: boolean;
    isSupplier?: boolean;
    priceListId?: string;
  },
) {
  return apiPost<{
    party: {
      id: string;
      name: string;
      isCustomer: boolean;
      isSupplier: boolean;
      priceListId: string | null;
    };
  }>(`/c/${companyRef}/parties`, body);
}

export function patchParty(
  companyRef: string,
  partyId: string,
  body: {
    isCustomer?: boolean;
    isSupplier?: boolean;
    priceListId?: string | null;
    /** Row watermark — plan/38 */
    expectedUpdatedAt?: string;
    /** Before snapshot for patched keys — plan/38 */
    base?: {
      isCustomer?: boolean;
      isSupplier?: boolean;
      priceListId?: string | null;
    };
  },
) {
  return apiPatch<{
    party: {
      id: string;
      isCustomer: boolean;
      isSupplier: boolean;
      priceListId: string | null;
      updatedAt?: string;
    };
  }>(`/c/${companyRef}/parties/${partyId}`, body);
}

export function deactivateParty(companyRef: string, partyId: string) {
  return apiPost<{ party: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/parties/${partyId}/deactivate`,
  );
}

export function reactivateParty(companyRef: string, partyId: string) {
  return apiPost<{ party: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/parties/${partyId}/reactivate`,
  );
}
